import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  GetApp as ExportIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Person as PersonIcon,
  Apps as ProductIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React, { useEffect, useState } from "react";
import { api } from "../../services/api";

import { useNavigate } from "react-router-dom";

const SessionViewerPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalEvents: 0,
    uniqueUsers: 0,
    uniqueProducts: 0,
    totalPages: 0,
    currentPage: 1,
  });
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    productId: "",
    userId: "",
    eventType: "all",
    page: 1,
    limit: 25,
    sortBy: "latest_event",
    sortOrder: "DESC",
  });
  const [sessionDetailsDialog, setSessionDetailsDialog] = useState({
    open: false,
    sessionId: null,
    events: [],
  });
  const [sessionEvents, setSessionEvents] = useState([]);
  const [loadingSessionDetails, setLoadingSessionDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSessions();
    fetchUsers();
    fetchProducts();
    fetchEventTypes();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/admin/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await api.get("/admin/event-types");
      setEventTypes(response.data);
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all" && value !== "") {
          if (key === "startDate" || key === "endDate") {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const response = await api.get(`/admin/sessions?${queryParams}`);
      setSessions(response.data.sessions);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      setLoadingSessionDetails(true);
      const response = await api.get(`/admin/sessions/${sessionId}/events`);
      setSessionEvents(response.data.events);
      setSessionDetailsDialog({
        open: true,
        sessionId,
        events: response.data.events,
      });
    } catch (error) {
      console.error("Error fetching session details:", error);
    } finally {
      setLoadingSessionDetails(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleUserSelectChange = (event, newValue) => {
    setSelectedUser(newValue);
    setFilters((prev) => ({
      ...prev,
      userId: newValue ? newValue.id : "",
      page: 1,
    }));
  };

  const handleProductSelectChange = (event, newValue) => {
    setSelectedProduct(newValue);
    setFilters((prev) => ({
      ...prev,
      productId: newValue ? newValue.id : "",
      page: 1,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };

  const handleRowsPerPageChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      limit: parseInt(event.target.value),
      page: 1,
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      productId: "",
      userId: "",
      eventType: "all",
      page: 1,
      limit: 25,
      sortBy: "latest_event",
      sortOrder: "DESC",
    });
    setSelectedUser(null);
    setSelectedProduct(null);
  };

  const handleExport = async (sessionId = null) => {
    try {
      setExporting(true);
      const queryParams = new URLSearchParams();

      if (sessionId) {
        queryParams.append("sessionId", sessionId);
      }

      if (filters.startDate) {
        queryParams.append("startDate", filters.startDate.toISOString());
      }
      if (filters.endDate) {
        queryParams.append("endDate", filters.endDate.toISOString());
      }
      if (filters.userId) {
        queryParams.append("userId", filters.userId);
      }
      if (filters.productId) {
        queryParams.append("productId", filters.productId);
      }
      if (filters.eventType && filters.eventType !== "all") {
        queryParams.append("eventType", filters.eventType);
      }

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const url = sessionId
        ? `/admin/sessions/${sessionId}/export`
        : `/admin/sessions/export?${queryParams}`;

      const response = await api.get(url, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = sessionId
        ? `session-${sessionId}-events-${
            new Date().toISOString().split("T")[0]
          }.csv`
        : `sessions-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting session data:", error);
      alert("Error exporting session data: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const toggleSessionExpansion = (sessionId) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      login: "success",
      logout: "warning",
      error: "error",
      click: "info",
      navigation: "default",
      download: "primary",
    };
    return colors[eventType] || "default";
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Session Viewer
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={resetFilters}
            startIcon={<RefreshIcon />}
          >
            Reset Filters
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleExport()}
            startIcon={<ExportIcon />}
            disabled={loading || exporting}
          >
            {exporting ? "Exporting..." : "Export All Sessions"}
          </Button>
          <Button
            variant="contained"
            onClick={fetchSessions}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Sessions
                  </Typography>
                  <Typography variant="h4">{stats.totalSessions}</Typography>
                </Box>
                <AssessmentIcon sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Events
                  </Typography>
                  <Typography variant="h4">{stats.totalEvents}</Typography>
                </Box>
                <EventIcon sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Unique Users
                  </Typography>
                  <Typography variant="h4">{stats.uniqueUsers}</Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: "info.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Unique Products
                  </Typography>
                  <Typography variant="h4">{stats.uniqueProducts}</Typography>
                </Box>
                <ProductIcon sx={{ fontSize: 40, color: "warning.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Accordion
        defaultExpanded
        sx={{ bgcolor: "background.default", border: "none" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange("startDate", date)}
                  slotProps={{ textField: { size: "small" } }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  slotProps={{ textField: { size: "small" } }}
                  onChange={(date) => handleFilterChange("endDate", date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={filters.eventType}
                  label="Event Type"
                  onChange={(e) =>
                    handleFilterChange("eventType", e.target.value)
                  }
                >
                  <MenuItem value="all">All Event Types</MenuItem>
                  {eventTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.email}
                value={selectedUser}
                onChange={handleUserSelectChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select User"
                    sx={{ width: "250px" }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => option.name}
                value={selectedProduct}
                onChange={handleProductSelectChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Product"
                    sx={{ width: "250px" }}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sessions Table */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Session ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Events Count</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session, index) => (
              <React.Fragment key={session.sessionId}>
                <TableRow
                  hover
                  sx={{
                    "&:hover": {
                      borderBottomColor: "primary.main",
                    },
                    "& td": {
                      py: 2,
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          toggleSessionExpansion(session.sessionId)
                        }
                      >
                        {expandedSessions.has(session.sessionId) ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {session.sessionId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`User ID: ${session.userId}`}>
                      <span style={{ cursor: "pointer" }}>
                        {session.userEmail}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {session.products && session.products.length > 0
                      ? session.products.map((product, index) => (
                          <Chip
                            key={index}
                            label={product}
                            size="small"
                            sx={{ mr: 1, mb: 0.5 }}
                          />
                        ))
                      : "No products"}
                  </TableCell>
                  <TableCell>{formatDate(session.startTime)}</TableCell>
                  <TableCell>{formatDate(session.lastActivity)}</TableCell>
                  <TableCell>
                    {formatDuration(session.durationSeconds)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={session.eventCount}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        onClick={() => fetchSessionDetails(session.sessionId)}
                        disabled={loadingSessionDetails}
                        size="small"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleExport(session.sessionId)}
                        disabled={exporting}
                        size="small"
                      >
                        <ExportIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={8}
                  >
                    <Collapse
                      in={expandedSessions.has(session.sessionId)}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Session Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">
                              Event Types:
                            </Typography>
                            {session.eventTypes &&
                              session.eventTypes.map((eventType, index) => (
                                <Chip
                                  key={index}
                                  label={`${eventType.type} (${eventType.count})`}
                                  color={getEventTypeColor(eventType.type)}
                                  size="small"
                                  sx={{ mr: 1, mb: 0.5 }}
                                />
                              ))}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">
                              Session Metadata:
                            </Typography>
                            <Typography variant="body2">
                              Hardware ID: {session.hardwareId || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              App Version: {session.appVersion || "N/A"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {sessions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No sessions found matching the current filters.
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={stats.totalSessions}
        rowsPerPage={filters.limit}
        page={stats.currentPage - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* Session Details Dialog */}
      <Dialog
        open={sessionDetailsDialog.open}
        onClose={() =>
          setSessionDetailsDialog({ open: false, sessionId: null, events: [] })
        }
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Session Events: {sessionDetailsDialog.sessionId}
        </DialogTitle>
        <DialogContent>
          {loadingSessionDetails ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : sessionEvents.length > 0 ? (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionEvents.map((event, index) => (
                    <TableRow
                      key={event.id}
                      hover
                      sx={{
                        "& td": {
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell>{formatDate(event.timestamp)}</TableCell>
                      <TableCell>
                        <Chip
                          label={event.event_type || event.eventType || "N/A"}
                          color={getEventTypeColor(
                            event.event_type || event.eventType,
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {event.product?.name || event.Product?.name || "N/A"}
                      </TableCell>
                      <TableCell>
                        {event.productVersion?.version ||
                          event.ProductVersion?.version ||
                          "N/A"}
                      </TableCell>
                      <TableCell>
                        <pre style={{ fontSize: "0.75rem", margin: 0 }}>
                          {event.data
                            ? JSON.stringify(event.data, null, 2)
                            : "N/A"}
                        </pre>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No events found for this session.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleExport(sessionDetailsDialog.sessionId)}
            disabled={exporting}
            startIcon={<ExportIcon />}
          >
            {exporting ? "Exporting..." : "Export Session Events"}
          </Button>
          <Button
            onClick={() =>
              setSessionDetailsDialog({
                open: false,
                sessionId: null,
                events: [],
              })
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SessionViewerPage;
